import { Box, Paper, Stack, Typography } from '@mui/material';
import { useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import {
  formatNumber,
  isEmptyArray,
  isNotNullOrUndefined,
  isPositive,
  withDefault,
} from '@vy/utils';
import {
  CUSTOMER_PORTAL_HEADING_COLOR,
  CUSTOMER_PORTAL_TEXT_COLOR,
} from '../customer-portal-colors';
import { setColorFromPalette } from './CustomerPortalUtils';

type CustomerPortalChartTooltipProps = {
  title?: string;
};

function getProductName(label: string | undefined | null, dataKey: string | undefined, index: number): string {
  if (isNotNullOrUndefined(label)) {
    return label;
  }

  const formattedProductName = `Product ${index + 1}`;

  return withDefault(dataKey, formattedProductName);
}

export const CustomerPortalChartTooltip: React.FC<CustomerPortalChartTooltipProps> = ({
  title = 'Usage breakdown',
}) => {
  const tooltipData = useAxesTooltip();

  if (!tooltipData || tooltipData.length === 0) {
    return null;
  }

  const { seriesItems } = tooltipData[0];

  const seriesWithData = seriesItems.filter((item) => {
    return typeof item.value === 'number' && isPositive(item.value as number);
  });

  const noDataForCurrentBar = isEmptyArray(seriesWithData);

  if (noDataForCurrentBar) {
    return null;
  }

  return (
    <Paper
      sx={{
        backgroundColor: '#FFFFFF',
        transform: 'translate(0%, -50%)',
        minWidth: '250px',
        maxWidth: '350px',
        position: 'relative',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.15)',
      }}
    >
      <Stack sx={{
        gap: 0
      }}>
        <Typography
          color={CUSTOMER_PORTAL_HEADING_COLOR}
          sx={{
            fontSize: "16px",
            lineHeight: "24px",
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            padding: "16px 16px 0px 16px"
          }}>
          {title}
        </Typography>

        <Stack sx={{
          padding: "8px 0"
        }}>
          {seriesWithData.map((item, index) => {
            const rawValue = item.value;
            if (typeof rawValue !== 'number') {
              return null;
            }

            const productName = getProductName(item.formattedLabel, undefined, index);
            const colorIndex = seriesItems.findIndex((s) => s.seriesId === item.seriesId);

            return (
              <Stack
                key={String(item.seriesId)}
                direction="row"
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "2px 16px",
                  gap: 2
                }}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: 0
                  }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '2px',
                      backgroundColor: setColorFromPalette(colorIndex),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    color={CUSTOMER_PORTAL_TEXT_COLOR}
                    noWrap
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      maxWidth: '180px'
                    }}>
                    {productName}
                  </Typography>
                </Stack>
                <Typography
                  color={CUSTOMER_PORTAL_HEADING_COLOR}
                  sx={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontWeight: 500,
                    textAlign: "right"
                  }}>
                  {formatNumber(rawValue)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};
